import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { purchaseItemService, PurchaseItemDTO } from '../../services/purchaseItemService';
import '../../pages/Products/ProductCategoryDeletePage.css';

const PurchaseItemDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<PurchaseItemDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadItem();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await purchaseItemService.getPurchaseItemById(parseInt(id || '0'));
      if (response?.data?.data) {
        setItem(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('purchaseItem.failedToLoadItem'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    setIsDeleting(true);
    try {
      await purchaseItemService.deletePurchaseItem(item.purchaseItemId);
      navigate('/purchase-items', { state: { message: t('purchaseItem.deletedSuccess') } });
    } catch (err) {
      setError(t('purchaseItem.failedToDeleteItem'));
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('purchaseItem.itemNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={() => navigate(`/purchase-items/${id}`)}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('purchaseItem.backToItem')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('purchaseItem.deleteConfirmationTitle')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('purchaseItem.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('purchaseItem.productName')}</label>
                  <p className="delete-info-value">{item.productName}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('purchaseItem.productSKU')}</label>
                  <p className="delete-info-value delete-code">{item.productSKU}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('purchaseItem.supplierName')}</label>
                  <p className="delete-info-value">{item.supplierName}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('purchaseItem.purchasePrice')}</label>
                  <p className="delete-info-value">
                    {Number(item.purchasePrice).toFixed(2)} {item.currency}
                  </p>
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate(`/purchase-items/${id}`)}
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('purchaseItem.deleteItem')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('purchaseItem.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('purchaseItem.permanentAction')}</strong>
                    <p>{t('purchaseItem.deleteWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('purchaseItem.relatedData')}</strong>
                    <p>{t('purchaseItem.deleteRelatedDataWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('purchaseItem.confirmationRequired')}</strong>
                    <p>{t('purchaseItem.deleteConfirmationRequired')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PurchaseItemDeletePage;
